import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Paper, Typography } from '@mui/material';
import {
  filterDataTableSuggestions,
  queryDataTableSuggestions,
  type DataTableSuggestQuery,
  type DataTableSuggestRequest,
  type DataTableSuggestionItem,
} from '../utils/dataTableReferences';

interface MenuState {
  items: DataTableSuggestionItem[];
  query: DataTableSuggestQuery;
  index: number;
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  above: boolean;
}

function isTextControl(el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

function suggestionText(query: DataTableSuggestQuery, item: DataTableSuggestionItem): string {
  return item.kind === 'request'
    ? `{{dt.${item.insert}.`
    : `{{dt.${query.requestToken}.${item.insert}}}`;
}

type SuggestEditor = {
  hasTextFocus: () => boolean;
  getModel: () => {
    getValue: () => string;
    getOffsetAt: (pos: { lineNumber: number; column: number }) => number;
    getPositionAt: (offset: number) => { lineNumber: number; column: number };
  } | null;
  getPosition: () => { lineNumber: number; column: number } | null;
  getScrolledVisiblePosition: (
    pos: { lineNumber: number; column: number }
  ) => { top: number; left: number; height: number } | null;
  getDomNode: () => HTMLElement | null;
  executeEdits: (source: string, edits: Array<{ range: object; text: string; forceMoveMarkers?: boolean }>) => void;
  setPosition: (pos: { lineNumber: number; column: number }) => void;
  focus: () => void;
};

const suggestEditors: SuggestEditor[] = [];

/** Request body uses Monaco, whose hidden textarea does not hold the full document. */
export function registerDataTableSuggestEditor(editor: object): () => void {
  const typed = editor as SuggestEditor;
  suggestEditors.push(typed);
  return () => {
    const index = suggestEditors.indexOf(typed);
    if (index >= 0) suggestEditors.splice(index, 1);
  };
}

function focusedSuggestEditor(): SuggestEditor | null {
  for (let i = 0; i < suggestEditors.length; i += 1) {
    const editor = suggestEditors[i];
    try {
      if (editor.hasTextFocus()) return editor;
    } catch {
      suggestEditors.splice(i, 1);
      i -= 1;
    }
  }
  return null;
}

function editorCaretRect(editor: SuggestEditor): { top: number; left: number; bottom: number } | null {
  const pos = editor.getPosition();
  const node = editor.getDomNode();
  if (!pos || !node) return null;
  const host = node.getBoundingClientRect();
  const scrolled = editor.getScrolledVisiblePosition(pos);
  if (!scrolled) {
    return { top: host.bottom, left: host.left, bottom: host.bottom };
  }
  const lineHeight = scrolled.height > 0 ? scrolled.height : 20;
  const top = host.top + scrolled.top;
  return { top, left: host.left + scrolled.left, bottom: top + lineHeight };
}

/** Place the list under the caret, not over the field text. Coordinates are viewport pixels. */
function inputCaretRect(el: HTMLInputElement | HTMLTextAreaElement): { top: number; left: number; bottom: number } {
  const field = (el.closest('.MuiInputBase-root') as HTMLElement | null) ?? el;
  const fieldRect = field.getBoundingClientRect();
  const inputRect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const mirror = document.createElement('span');
  const caret = el.selectionStart ?? el.value.length;
  mirror.textContent = el.value.slice(0, caret).replace(/ /g, '\u00a0') || '\u00a0';
  mirror.style.position = 'fixed';
  mirror.style.left = '-9999px';
  mirror.style.top = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre';
  mirror.style.font = style.font;
  mirror.style.letterSpacing = style.letterSpacing;
  document.body.appendChild(mirror);
  const textWidth = mirror.getBoundingClientRect().width;
  mirror.remove();
  const padLeft = parseFloat(style.paddingLeft) || 0;
  const left = inputRect.left + padLeft + textWidth - el.scrollLeft;
  return { top: fieldRect.top, left, bottom: fieldRect.bottom };
}

function applyEditorSuggestion(editor: SuggestEditor, query: DataTableSuggestQuery, item: DataTableSuggestionItem) {
  const model = editor.getModel();
  if (!model) return;
  const insertion = suggestionText(query, item);
  const start = model.getPositionAt(query.tokenStart);
  const end = model.getPositionAt(query.tokenEnd);
  editor.executeEdits('dt-suggest', [
    {
      range: {
        startLineNumber: start.lineNumber,
        startColumn: start.column,
        endLineNumber: end.lineNumber,
        endColumn: end.column,
      },
      text: insertion,
      forceMoveMarkers: true,
    },
  ]);
  const caret = model.getPositionAt(query.tokenStart + insertion.length);
  editor.setPosition(caret);
  editor.focus();
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, 'value');
  desc?.set?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function applySuggestion(
  el: HTMLInputElement | HTMLTextAreaElement,
  query: DataTableSuggestQuery,
  item: DataTableSuggestionItem
) {
  const text = el.value ?? '';
  const before = text.slice(0, query.tokenStart);
  const after = text.slice(query.tokenEnd);
  const insertion = suggestionText(query, item);
  const next = before + insertion + after;
  const caret = before.length + insertion.length;
  setNativeValue(el, next);
  requestAnimationFrame(() => {
    el.focus();
    try {
      el.setSelectionRange(caret, caret);
    } catch {
      /* some input types reject a selection range */
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/**
 * While a field contains `{{dt.` lists collection requests.
 * `{{dt.request.` lists that request’s data columns.
 */
export default function DataTableMustacheSuggestions({
  requests,
}: {
  requests: DataTableSuggestRequest[];
}) {
  const [menu, setMenu] = useState<MenuState | null>(null);
  const requestsRef = useRef(requests);
  const menuRef = useRef(menu);
  const listRef = useRef<HTMLDivElement | null>(null);
  const dismissedKey = useRef<string | null>(null);
  requestsRef.current = requests;
  menuRef.current = menu;

  useEffect(() => {
    const openMenu = (
      query: ReturnType<typeof queryDataTableSuggestions>,
      anchorTop: number,
      anchorBottom: number,
      anchorLeft: number
    ) => {
      if (!query) {
        dismissedKey.current = null;
        setMenu(null);
        return;
      }
      const signature = `${query.kind}|${query.requestToken ?? ''}|${query.filter}`;
      if (dismissedKey.current === signature) {
        setMenu(null);
        return;
      }
      dismissedKey.current = null;
      const items = filterDataTableSuggestions(query, requestsRef.current);
      if (items.length === 0) {
        setMenu(null);
        return;
      }
      const menuWidth = Math.min(420, Math.max(280, window.innerWidth - 16));
      const gap = 6;
      const spaceBelow = Math.max(0, window.innerHeight - anchorBottom - gap - 8);
      const spaceAbove = Math.max(0, anchorTop - gap - 8);
      const above = spaceBelow < 180 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(120, Math.min(360, above ? spaceAbove : spaceBelow));
      const left = Math.max(8, Math.min(anchorLeft, window.innerWidth - menuWidth - 8));
      setMenu((prev) => ({
        items,
        query,
        index:
          prev && prev.query.kind === query.kind && prev.query.filter === query.filter
            ? Math.min(prev.index, items.length - 1)
            : 0,
        top: above ? anchorTop : anchorBottom + gap,
        left,
        width: menuWidth,
        maxHeight,
        above,
      }));
    };

    const refresh = () => {
      const editor = focusedSuggestEditor();
      if (editor) {
        const model = editor.getModel();
        const pos = editor.getPosition();
        if (!model || !pos) {
          setMenu(null);
          return;
        }
        const caret = model.getOffsetAt(pos);
        const rect = editorCaretRect(editor);
        openMenu(queryDataTableSuggestions(model.getValue(), caret), rect?.top ?? 0, rect?.bottom ?? 0, rect?.left ?? 8);
        return;
      }
      const el = document.activeElement;
      if (!isTextControl(el) || el.disabled || el.readOnly || el.closest('.monaco-editor')) {
        setMenu(null);
        return;
      }
      const type = el instanceof HTMLInputElement ? (el.type || 'text').toLowerCase() : 'text';
      if (type === 'password' || type === 'hidden' || type === 'checkbox' || type === 'radio') {
        setMenu(null);
        return;
      }
      const caret = el.selectionStart ?? el.value.length;
      const rect = inputCaretRect(el);
      openMenu(queryDataTableSuggestions(el.value ?? '', caret), rect.top, rect.bottom, rect.left);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const current = menuRef.current;
      const el = document.activeElement;
      if (!current || !isTextControl(el)) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        setMenu((prev) => {
          if (!prev) return prev;
          const delta = event.key === 'ArrowDown' ? 1 : -1;
          const index = (prev.index + delta + prev.items.length) % prev.items.length;
          return { ...prev, index };
        });
        return;
      }
      if (event.key === 'Escape') {
        const current = menuRef.current;
        if (current) {
          dismissedKey.current = `${current.query.kind}|${current.query.requestToken ?? ''}|${current.query.filter}`;
        }
        event.preventDefault();
        setMenu(null);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const item = current.items[current.index];
        if (!item) return;
        event.preventDefault();
        event.stopPropagation();
        const editor = focusedSuggestEditor();
        if (editor) {
          applyEditorSuggestion(editor, current.query, item);
          return;
        }
        if (isTextControl(el)) applySuggestion(el, current.query, item);
      }
    };

    document.addEventListener('input', refresh, true);
    document.addEventListener('keyup', refresh, true);
    document.addEventListener('click', refresh, true);
    document.addEventListener('focusin', refresh, true);
    document.addEventListener('dt-suggest-refresh', refresh);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', refresh, true);
    window.addEventListener('resize', refresh);
    return () => {
      document.removeEventListener('input', refresh, true);
      document.removeEventListener('keyup', refresh, true);
      document.removeEventListener('click', refresh, true);
      document.removeEventListener('focusin', refresh, true);
      document.removeEventListener('dt-suggest-refresh', refresh);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', refresh, true);
      window.removeEventListener('resize', refresh);
    };
  }, []);

  useEffect(() => {
    document.dispatchEvent(new Event('dt-suggest-refresh'));
  }, [requests]);

  useEffect(() => {
    const selected = listRef.current?.querySelector('[aria-selected="true"]');
    selected?.scrollIntoView({ block: 'nearest' });
  }, [menu?.index, menu?.items.length]);

  if (!menu) return null;

  const menuNode = (
    <Paper
      elevation={6}
      role="listbox"
      aria-label={menu.query.kind === 'request' ? 'Requests' : 'Data columns'}
      sx={{
        position: 'fixed',
        top: menu.above ? undefined : menu.top,
        bottom: menu.above ? `${window.innerHeight - menu.top + 4}px` : undefined,
        left: menu.left,
        width: menu.width,
        zIndex: 16000,
        maxHeight: menu.maxHeight,
        overflowX: 'hidden',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        scrollbarWidth: 'thin',
        py: 0.5,
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.35)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.06)' },
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', px: 1.25, py: 0.5, color: 'text.secondary' }}>
        {menu.query.kind === 'request' ? 'Requests' : 'Data columns'}
      </Typography>
      <Box ref={listRef}>
      {menu.items.map((item, index) => (
        <Box
          key={`${item.kind}-${item.insert}`}
          role="option"
          aria-selected={index === menu.index}
          onMouseDown={(event) => {
            event.preventDefault();
            const editor = focusedSuggestEditor();
            if (editor) {
              applyEditorSuggestion(editor, menu.query, item);
              return;
            }
            const el = document.activeElement;
            if (!isTextControl(el)) return;
            applySuggestion(el, menu.query, item);
          }}
          onMouseEnter={() => setMenu((prev) => (prev ? { ...prev, index } : prev))}
          sx={{
            px: 1.25,
            py: 0.6,
            cursor: 'pointer',
            bgcolor: index === menu.index ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-word' }}>
            {item.kind === 'request' ? item.insert : item.label}
          </Typography>
          {item.kind === 'request' && item.label !== item.insert && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
              {item.label}
            </Typography>
          )}
        </Box>
      ))}
      </Box>
    </Paper>
  );

  return createPortal(menuNode, document.body);
}
